<script setup lang="ts">
  import PaperclipIcon from '@/shared/assets/icons/PaperclipIcon.svg';
  import {
    DOCUMENT_SEND_QUALITY_RECORD,
    type DocumentSendRecipientReport,
    DocumentSendFormat,
    type DocumentSendRecipientType,
  } from '@/entities/DocumentSend';
  import { formatInstantToPtBR } from '@/shared/lib';
  import { IconImage, ModalDocumentInfo } from '@/shared/ui';
  import { useBranchLogo } from '@/entities/Branch';

  defineProps<{
    report: DocumentSendRecipientReport;
  }>();

  const { currentLogoUrl, handleLogoError } = useBranchLogo();

  const defaultBoxClass =
    'flex min-h-11 items-center rounded-lg border border-gray-02 bg-gray-02/20 px-4 py-2 text-sm text-gray-07';
  const centeredBoxClass = `${defaultBoxClass} justify-center`;

  const formatLabels: Record<DocumentSendFormat, string> = {
    [DocumentSendFormat.ELECTRONIC]: 'Eletrônico',
    [DocumentSendFormat.PHYSICAL]: 'Físico',
    [DocumentSendFormat.ELECTRONIC_PHYSICAL]: 'Eletrônico e Físico',
  };

  const recipientTypeLabels: Record<DocumentSendRecipientType, string> = {
    CLIENT: 'Cliente',
    SUPPLIER: 'Fornecedor',
  };
</script>

<template>
  <div
    class="overflow-hidden rounded-2xl border border-gray-02 bg-white-01"
    data-testid="document-send-recipient-report-result"
  >
    <div
      id="document-send-report-content"
      class="px-6 py-6"
    >
      <ModalDocumentInfo
        :document-info="DOCUMENT_SEND_QUALITY_RECORD"
        title="CONTROLE DE ENVIO DE DOCUMENTOS"
        :logo-url="currentLogoUrl"
        @logo-error="handleLogoError"
      />

      <section class="-mx-6 space-y-3 border-t border-gray-02 p-6">
        <h2 class="text-sm font-semibold text-gray-07">
          Destinatário:
        </h2>
        <div class="grid gap-6 md:grid-cols-[200px_minmax(0,1fr)_280px]">
          <div class="flex flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Tipo de Destinatário</span>
            <div :class="defaultBoxClass">
              {{ recipientTypeLabels[report.recipient.recipientType] }}
            </div>
          </div>

          <div class="flex min-w-0 flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Nome do Destinatário</span>
            <div :class="defaultBoxClass">
              {{ report.recipient.name }}
            </div>
          </div>

          <div class="flex min-w-0 flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">E-mail</span>
            <div :class="`${defaultBoxClass} min-w-0`">
              <span class="block min-w-0 truncate">{{ report.recipient.email ?? '-' }}</span>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="report.sends.length === 0"
        class="-mx-6 border-t border-gray-02 px-6 py-6"
      >
        <p class="text-sm text-gray-07">
          Nenhum envio encontrado para o destinatário selecionado.
        </p>
      </section>

      <article
        v-for="(send, index) in report.sends"
        :key="send.id"
        class="-mx-6 space-y-6 border-t border-gray-02 px-6 py-6"
        :data-testid="`document-send-recipient-report-item-${send.id}`"
      >
        <h3 class="text-sm font-semibold text-gray-07">
          Envio {{ String(index + 1).padStart(2, '0') }}
        </h3>

        <div class="grid gap-6 md:grid-cols-[minmax(0,1fr)_220px]">
          <div class="flex min-w-0 flex-col gap-2">
            <span class="text-sm font-semibold text-gray-07">Título</span>
            <div :class="defaultBoxClass">
              {{ send.title }}
            </div>
          </div>

          <div class="flex flex-col gap-2 md:w-56">
            <span class="text-sm font-semibold text-gray-07">Formato</span>
            <div :class="defaultBoxClass">
              {{ formatLabels[send.format] }}
            </div>
          </div>
        </div>

        <section class="space-y-3">
          <h4 class="text-sm font-semibold text-gray-07">
            Documento:
          </h4>
          <div class="space-y-4">
            <div
              v-for="(documentItem, documentIndex) in send.documents"
              :key="`${send.id}-document-${documentIndex}`"
              class="grid gap-6 md:grid-cols-[140px_minmax(0,1fr)_96px_96px]"
            >
              <div class="flex flex-col gap-2">
                <span class="text-sm font-semibold text-gray-07">Prefixo</span>
                <div :class="defaultBoxClass">
                  {{ documentItem.prefix.trim() ? documentItem.prefix : '-' }}
                </div>
              </div>

              <div class="flex min-w-0 flex-col gap-2">
                <span class="text-sm font-semibold text-gray-07">Título</span>
                <div :class="defaultBoxClass">
                  {{ documentItem.title ?? '-' }}
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <span class="text-sm font-semibold text-gray-07">Revisão</span>
                <div :class="centeredBoxClass">
                  {{ String(documentItem.revision).padStart(2, '0') }}
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <span class="text-sm font-semibold text-gray-07">Cópias</span>
                <div :class="centeredBoxClass">
                  {{
                    documentItem.copies === null
                      ? '-'
                      : String(documentItem.copies).padStart(2, '0')
                  }}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="send.attachments.length > 0"
          class="space-y-3"
        >
          <h4 class="text-sm font-semibold text-gray-07">
            Anexos:
          </h4>
          <div class="space-y-2">
            <div
              v-for="(attachment, attachmentIndex) in send.attachments"
              :key="`${send.id}-attachment-${attachmentIndex}`"
              class="flex min-h-11 items-center gap-3 rounded-lg border border-gray-02 bg-gray-02/20 px-4 py-2 text-sm text-gray-07"
            >
              <IconImage
                :src="PaperclipIcon"
                alt="Anexo"
                class="shrink-0"
              />
              <a
                v-if="attachment.sourceType === 'URL' && attachment.externalUrl"
                :href="attachment.externalUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="truncate text-[var(--theme-secondary-ink)] underline"
              >
                {{ attachment.fileName }}
              </a>
              <span
                v-else
                class="truncate"
              >{{ attachment.fileName }}</span>
            </div>
          </div>
        </section>

        <section class="space-y-3">
          <h4 class="text-sm font-semibold text-gray-07">
            Remetente
          </h4>
          <div class="grid gap-6 md:grid-cols-3">
            <div class="flex flex-col gap-2">
              <span class="text-sm font-semibold text-gray-07">Responsável pelo envio</span>
              <div :class="defaultBoxClass">
                {{ send.sender.name }}
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <span class="text-sm font-semibold text-gray-07">Cargo</span>
              <div :class="defaultBoxClass">
                {{ send.sender.position }}
              </div>
            </div>

            <div class="flex flex-col gap-2">
              <span class="text-sm font-semibold text-gray-07">Data de envio</span>
              <div :class="defaultBoxClass">
                {{ formatInstantToPtBR(send.sender.sendDate, { withTime: false }) }}
              </div>
            </div>
          </div>
        </section>
      </article>
    </div>
  </div>
</template>
